import React, { useMemo } from "react";
import { Checkbox, IconButton, Stack, Tooltip } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import GestionListPagination from "components/pages/Gestion/GestionListPagination";
import GestionTable from "components/pages/Gestion/GestionTable";
import RevaText from "components/common/RevaText";
import { chargeTypeLabelId, isKnownChargeType } from "constants/external-charge-types";
import { formatFinancialAmount, invoiceLedgerDate } from "../financial/financial-helpers";
import { invoiceOriginMessage } from "./invoice-origin";

export const isPendingLink = (invoice) =>
  ["pending", "PENDING"].includes(invoice.payment_status) &&
  (!invoice.pending_expires_at || dayjs(invoice.pending_expires_at).isAfter(dayjs()));

export const isPayableInvoice = (invoice) =>
  ["open", "OPEN"].includes(invoice.status) && !invoice.payment_id && !isPendingLink(invoice);

const invoiceDescription = (invoice) =>
  invoice.description || invoice.invoice_items?.[0]?.description || `#${invoice.id}`;

const VoidedStatus = ({ invoice, label, expiresAt = null }) => {
  const intl = useIntl();
  // System voids carry a reason but no user: show the reason alone, not "reason — —".
  const reasonLine = invoice.void_reason
    ? invoice.voided_by_name
      ? intl.formatMessage(
          { id: "gestion.playerFinance.voidedDetail" },
          { reason: invoice.void_reason, user: invoice.voided_by_name },
        )
      : invoice.void_reason
    : null;
  const voidedOn = invoice.voided_at ? dayjs(invoice.voided_at).format("DD/MM/YYYY") : null;
  return (
    <Stack spacing={0.25}>
      <RevaText>{label}</RevaText>
      {reasonLine && (
        <RevaText variant="caption" color="text.secondary">
          {reasonLine}
        </RevaText>
      )}
      {voidedOn && (
        <RevaText variant="caption" color="text.secondary">
          {intl.formatMessage({ id: "gestion.playerFinance.voidedOn" }, { date: voidedOn })}
        </RevaText>
      )}
      {expiresAt && (
        <RevaText variant="caption" color="text.secondary">
          {intl.formatMessage(
            { id: "gestion.playerFinance.linkExpiresAt" },
            { time: dayjs(expiresAt).format("HH:mm") },
          )}
        </RevaText>
      )}
    </Stack>
  );
};

const PlayerAccountLedger = ({
  invoices,
  membershipOnly,
  selectedIds,
  onSelectedIdsChange,
  currency,
  pagination = null,
  onVoid = null,
  onCopyLink = null,
  onVoidLink = null,
}) => {
  const intl = useIntl();
  const payableIds = useMemo(() => invoices.filter(isPayableInvoice).map((invoice) => invoice.id), [invoices]);
  const selectedPayableCount = payableIds.filter((id) => selectedIds.includes(id)).length;
  const allSelected = payableIds.length > 0 && selectedPayableCount === payableIds.length;

  const selectAll = () => {
    const outsidePage = selectedIds.filter((id) => !payableIds.includes(id));
    onSelectedIdsChange(allSelected ? outsidePage : [...outsidePage, ...payableIds]);
  };

  const rows = invoices.map((invoice) => ({
    ...invoice,
    date: invoiceLedgerDate(invoice, intl, membershipOnly),
    description: invoiceDescription(invoice),
    origin: invoiceOriginMessage(invoice, intl),
    type: isKnownChargeType(invoice.charge_type)
      ? intl.formatMessage({ id: chargeTypeLabelId(invoice.charge_type) })
      : invoice.charge_type || "—",
    amount: formatFinancialAmount(invoice.final_amount, currency),
    displayStatus: isPendingLink(invoice)
      ? intl.formatMessage({ id: "gestion.playerFinance.statusLinkPending" })
      : intl.formatMessage({ id: `gestion.playerFinance.status.${String(invoice.status || "open").toLowerCase()}` }),
  }));

  return (
    <Stack spacing={2}>
      <GestionTable
        headers={[
          {
            key: "select",
            title: (
              <Checkbox
                checked={allSelected}
                indeterminate={selectedPayableCount > 0 && !allSelected}
                disabled={payableIds.length === 0}
                onChange={selectAll}
              />
            ),
            padding: "checkbox",
            width: 48,
            renderCell: (_value, invoice) => (
              <Checkbox
                checked={selectedIds.includes(invoice.id)}
                disabled={!isPayableInvoice(invoice)}
                onChange={() =>
                  onSelectedIdsChange(
                    selectedIds.includes(invoice.id)
                      ? selectedIds.filter((id) => id !== invoice.id)
                      : [...selectedIds, invoice.id],
                  )
                }
              />
            ),
          },
          { key: "date", title: intl.formatMessage({ id: "gestion.playerFinance.date" }) },
          { key: "description", title: intl.formatMessage({ id: "gestion.playerFinance.description" }) },
          { key: "origin", title: intl.formatMessage({ id: "gestion.playerFinance.origin" }) },
          { key: "type", title: intl.formatMessage({ id: "gestion.playerFinance.type" }) },
          { key: "amount", title: intl.formatMessage({ id: "gestion.playerFinance.amount" }), align: "right" },
          {
            key: "displayStatus",
            title: intl.formatMessage({ id: "gestion.playerFinance.status" }),
            renderCell: (label, invoice) => (
              <VoidedStatus
                invoice={invoice}
                label={label}
                expiresAt={isPendingLink(invoice) ? invoice.pending_expires_at : null}
              />
            ),
          },
          ...(onVoid || onVoidLink || onCopyLink
            ? [
                {
                  key: "actions",
                  title: "",
                  width: 96,
                  padding: "checkbox",
                  renderCell: (_value, invoice) => {
                    if (isPayableInvoice(invoice) && onVoid) {
                      return (
                        <Tooltip title={intl.formatMessage({ id: "gestion.playerFinance.void" })}>
                          <IconButton size="small" color="error" onClick={() => onVoid(invoice)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      );
                    }
                    if (isPendingLink(invoice) && invoice.payment_id) {
                      return (
                        <Stack direction="row" spacing={0.5}>
                          {onCopyLink && invoice.payment_url ? (
                            <Tooltip title={intl.formatMessage({ id: "gestion.playerFinance.copyLink" })}>
                              <IconButton
                                size="small"
                                data-testid={`copy-link-${invoice.id}`}
                                onClick={() => onCopyLink(invoice)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {onVoidLink ? (
                            <Tooltip title={intl.formatMessage({ id: "gestion.playerFinance.voidLink" })}>
                              <IconButton
                                size="small"
                                color="error"
                                data-testid={`void-link-${invoice.id}`}
                                onClick={() => onVoidLink(invoice)}
                              >
                                <LinkOffIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      );
                    }
                    return null;
                  },
                },
              ]
            : []),
        ]}
        rows={rows}
        emptyMessage={intl.formatMessage({ id: "gestion.playerFinance.empty" })}
      />
      {pagination && (
        <GestionListPagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          totalResults={pagination.totalResults}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
          disabled={pagination.disabled}
        />
      )}
      {selectedIds.length > 0 && (
        <RevaText variant="caption">
          {intl.formatMessage({ id: "gestion.playerFinance.selectedCount" }, { count: selectedIds.length })}
        </RevaText>
      )}
    </Stack>
  );
};

export default PlayerAccountLedger;
